import moment from "moment";
import React from "react";

type Stop = {
  distance: number;
  duration: number;
};

interface Props {
  stops: Stop[];
  duration?: string; // Ex: "5h57min"
}

const formatFinalTime = (seconds: number): string => {
  const duration = moment.duration(seconds, "seconds");
  const hour = duration.hours();
  const min = duration.minutes();

  return hour > 0 ? `${hour}:${String(min).padStart(2, "0")}min` : `${String(min).padStart(2, "0")}min`;
};

const formatIntermediateTime = (seconds: number): string => {
  const duration = moment.duration(seconds, "seconds");
  const hour = duration.hours();
  const min = duration.minutes();

  return hour > 0 ? `${hour}h${String(min).padStart(2, "0")}min` : `${String(min).padStart(2, "0")}min`;
};

const parseDurationStringToSeconds = (durationStr?: string): number => {
  if (!durationStr) return 0;
  const match = durationStr.match(/(\d+)h(\d+)min/);
  if (match) {
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    return hours * 3600 + minutes * 60;
  }
  const minMatch = durationStr.match(/(\d+)min/);
  if (minMatch) {
    const minutes = parseInt(minMatch[1], 10);
    return minutes * 60;
  }
  return 0;
};

const RouteV2 = ({ stops, duration }: Props) => {
  const pinPositions: number[] = [];

  const durationTotalSeconds = parseDurationStringToSeconds(duration);

  let accumulated = 0;
  let lastRightEdge = -10;
  const minSpacingPercent = 6;

  const estimateLabelWidthPercent = (label: string): number => {
    const charWidthPx = 7.5;
    const labelPx = label.length * charWidthPx;
    const timelineWidthPx = 1000;
    return (labelPx / timelineWidthPx) * 100;
  };

  const pins: any = (Array.isArray(stops) ? stops : []).map((step, index) => {
    // Para o primeiro pin (origem), o label é "0h00min" e posição é 0%
    if (index === 0) {
      const label = formatIntermediateTime(0);
      const labelWidthPercent = estimateLabelWidthPercent(label);

      lastRightEdge = 0 + labelWidthPercent;
      pinPositions.push(0);

      // Acumula para o próximo cálculo
      accumulated += step.duration;

      return {
        label,
        iconClass: "rioglyph-start",
        leftPercent: 0,
        labelPadding: { paddingLeft: 25 },
        iconOffset: 3,
      };
    }

    // Para pins de paradas (não origem, não destino)
    const label = formatIntermediateTime(accumulated);
    const labelWidthPercent = estimateLabelWidthPercent(label);

    const baseLeft = durationTotalSeconds === 0 ? 0 : (accumulated / durationTotalSeconds) * 100;
    const proposedLeft = baseLeft < lastRightEdge + minSpacingPercent ? lastRightEdge + minSpacingPercent : baseLeft;

    lastRightEdge = proposedLeft + labelWidthPercent;
    pinPositions.push(proposedLeft);

    // Acumula para o próximo cálculo
    accumulated += step.duration;

    return {
      label,
      iconClass: "rioglyph-pushpin",
      leftPercent: proposedLeft,
      labelPadding: { paddingLeft: 10 },
      iconOffset: 10,
    };
  });

  // Final pin (based on total duration, not stops)
  const finalLabel = formatFinalTime(durationTotalSeconds);
  //   const finalLabel = formatFinalTime(stepsDurationSeconds);

  pinPositions.push(100);
  pins.push({
    label: finalLabel,
    iconClass: "rioglyph-finish",
    leftPercent: 100,
    labelPadding: { paddingRight: 0 },
    iconOffset: -10,
  });

  return (
    <div className="position-relative width-100pct display-flex align-items-start" style={{ height: 50 }}>
      {pins.map((pin: any, index: number) => (
        <div
          key={index}
          className="position-absolute display-flex flex-column align-items-center"
          style={{
            left: `${pin.leftPercent}%`,
            transform: "translateX(-50%)",
            top: 0,
          }}
        >
          <span className="text-color-dark text-size-14 margin-bottom-1" style={pin.labelPadding}>
            {pin.label}
          </span>
          <span
            className={`rioglyph ${pin.iconClass} text-color-dark text-size-14`}
            style={{ marginTop: 0, marginLeft: pin.iconOffset }}
          />
        </div>
      ))}

      {/* Linha horizontal */}
      <div
        className="position-absolute width-100pct bg-highlight-light height-10"
        style={{
          bottom: "0%",
          transform: "translateY(-50%)",
        }}
      />

      {/* Risquinhos verticais */}
      {pinPositions.map((left, i) => (
        <div
          key={`tick-${i}`}
          className={`position-absolute bg-dark ${i === 0 || i === pinPositions.length - 1 ? "width-4" : "width-3"}`}
          style={{
            height: 10,
            bottom: "0%",
            transform: "translate(-50%, -50%)",
            left: `${left}%`,
          }}
        />
      ))}
    </div>
  );
};

export default RouteV2;
